import { google, office365 } from 'calendar-link';

import { getBrand } from 'flow/brands/brands';

interface I18next {
    t: (key: string) => string;
}

interface BuildScheduleUrlParams {
    roomName: string;
    joinLink: string;
    number?: string;
    pin?: string;
    i18next: I18next;
    start?: Date;
}

const MEETING_DURATION_MINUTES = 30;

// Avoids scheduling a meeting that's already in the past by the time the organizer finishes the compose flow.
export const nextHalfHour = (): Date => {
    const date = new Date();
    date.setSeconds(0, 0);
    date.setMinutes(date.getMinutes() < 30 ? 30 : 0, 0, 0);
    if (date.getMinutes() === 0) {
        date.setHours(date.getHours() + 1);
    }
    return date;
};

// Room name, joining info (link, phone number, PIN), and any brand extra-numbers link — shared body for the calendar-link event across providers.
const buildScheduleDescription = ({
    joinLink,
    number,
    pin,
    i18next,
}: Pick<BuildScheduleUrlParams, 'joinLink' | 'number' | 'pin' | 'i18next'>) => {
    const moreNumbersString = getBrand().brandConfNumberUrl
        ? `${i18next.t('videoconference.email.body.more_numbers')} ${getBrand().brandConfNumberUrl}`
        : null;

    return [
        i18next.t('videoconference.email.body.invitation'),
        `${i18next.t('videoconference.email.body.join_video')} ${joinLink}`,
        number && pin
            ? `${i18next.t('videoconference.email.body.join_phone')} ${number}, ${pin}`
            : null,
        moreNumbersString,
    ]
        .filter(Boolean)
        .join('\n\n');
};

/**
 * Builds an Outlook "new event" deep link pre-filled with the meeting room
 * name as the subject and the Telavox joining info (link, phone number,
 * PIN) in the event body.
 *
 * Uses calendar-link's office365() (outlook.office.com), not outlook()
 * (outlook.live.com/personal Outlook.com) — see BookMeeting.tsx, which
 * offers both as distinct options for exactly this reason: they're
 * different services with separate sessions. "Linked Microsoft account"
 * here gates a business-context integration, so a work/school Microsoft
 * 365 account is virtually always what's linked, not a personal one.
 */
export const buildOutlookScheduleUrl = ({
    roomName,
    joinLink,
    number,
    pin,
    i18next,
    start = nextHalfHour(),
}: BuildScheduleUrlParams): string =>
    office365({
        title: roomName,
        description: buildScheduleDescription({
            joinLink,
            number,
            pin,
            i18next,
        }),
        start,
        duration: [MEETING_DURATION_MINUTES, 'minutes'],
        location: joinLink,
    });

/**
 * Same as buildOutlookScheduleUrl, for Google Calendar's "new event" compose
 * page — no account linking required, unlike Outlook, since it's just a
 * pre-filled URL opened in the organizer's own Google session.
 */
export const buildGoogleScheduleUrl = ({
    roomName,
    joinLink,
    number,
    pin,
    i18next,
    start = nextHalfHour(),
}: BuildScheduleUrlParams): string =>
    google({
        title: roomName,
        description: buildScheduleDescription({
            joinLink,
            number,
            pin,
            i18next,
        }),
        start,
        duration: [MEETING_DURATION_MINUTES, 'minutes'],
        location: joinLink,
    });
